import Connection from "./connection";
import Node from "./node";
import InnovationDB from './innovatinoDB';
import NeatParameters from "./parameters";

import * as util from "./util";

class Network {
  private inputNumber: number;
  private outputNumber: number;

  public connections: Connection[];
  public sortedNodes: Node[];

  private inputNodes: Node[];
  private hiddenNodes: Node[];
  private outputNodes: Node[];
  private nextNode: number;
  private innovDB: InnovationDB;

  protected fitness: number | null;

  constructor(inputNum: number, outputNum: number, innovDB: InnovationDB) {
    this.inputNumber = inputNum;
    this.outputNumber = outputNum;

    this.inputNodes = [];
    this.hiddenNodes = [];
    this.outputNodes = [];

    this.innovDB = innovDB;

    this.connections = [];
    this.nextNode = 0;
    this.fitness = null;

    for (let i = 0; i < inputNum; i++) {
      const inputNode = new Node(this.nextNode++, "input");
      this.inputNodes.push(inputNode);
    }

    for (let i = 0; i < outputNum; i++) {
      const outputNode = new Node(this.nextNode++, "output");
      this.outputNodes.push(outputNode);
    }

    for (let i = 0; i < inputNum; i++) {
      for (let j = 0; j < outputNum; j++) {
        const connection = new Connection(
          this.inputNodes[i],
          this.outputNodes[j],
          this.innovDB
        );

        this.connections.push(connection);
      }
    }

    this.sortedNodes = [];
    this.sort();
  }

  addConnection(connection: Connection): boolean {
    if (
      connection.to.type === "input" ||
      connection.from.type === "output" ||
      this.connections.some(
        (c) => c.from === connection.from && c.to === connection.to
      )
    ) {
      return false;
    }

    this.connections.push(connection);
    if (this.sort()) {
      return true;
    } else {
      this.connections.splice(this.connections.indexOf(connection), 1);
      return false;
    }
  }

  addNodeTo(connection: Connection): Node {
    const node = new Node(this.nextNode++, "hidden");
    this.addConnection(
      new Connection(connection.from, node, this.innovDB, connection.weight)
    );
    this.addConnection(new Connection(node, connection.to, this.innovDB, 1));

    connection.enabled = false;
    this.hiddenNodes.push(node);

    this.sort();
    return node;
  }

  // Topologically sort network graph
  sort(): boolean {
    const result = [];
    const q = [...this.inputNodes];
    const connections = [...this.connections];

    let a;
    while ((a = q.pop()) !== undefined) {
      result.push(a);

      for (let i = 0; i < connections.length; i++) {
        if (connections[i].from === a) {
          const m = connections.splice(i, 1)[0];
          i--;

          if (!connections.some((connection) => connection.to === m.to)) {
            q.push(m.to);
          }
        }
      }
    }

    if (connections.length === 0) {
      this.sortedNodes = result;
      return true;
    } else return false; // Network has at least one cycle
  }

  mutate(params: NeatParameters): void {
    if (Math.random() < params.mutation.weight) {
      for (const connection of this.connections) {
        const rand2 = Math.random();

        if (rand2 < params.mutation.weightPerturbe) {
          connection.weight += util.uniformRandom() / 50;
        } else {
          connection.weight = util.uniformRandom();
        }
      }
    }

    if (Math.random() < params.mutation.bias) {
      for (const node of this.hiddenNodes) {
        const rand2 = Math.random();

        if (rand2 < params.mutation.biasPerturbe) {
          node.bias += util.uniformRandom() / 50;
        } else {
          node.bias = util.uniformRandom();
        }
      }
    }

    if (Math.random() < params.mutation.addConnection) {
      if (!this.isFullyConnected()) {
        const nodes = this.inputNodes.concat(
          this.hiddenNodes,
          this.outputNodes
        );

        while (
          !this.addConnection(
            new Connection(util.randomChoice(nodes), util.randomChoice(nodes), this.innovDB)
          )
        );
      }
    }

    if (Math.random() < params.mutation.addNode) {
      if (this.connections.some((c) => c.enabled)) {
        let connection;
        while (!(connection = util.randomChoice(this.connections)).enabled);

        this.addNodeTo(connection);
      }
    }

    if (Math.random() < params.mutation.toggleConnection) {
      const connection = util.randomChoice(this.connections);
      connection.enabled = !connection.enabled;
    }
  }

  isFullyConnected(): boolean {
    let maxConnection = 0;
    maxConnection +=
      this.inputNodes.length *
      (this.hiddenNodes.length + this.outputNodes.length);
    maxConnection += this.hiddenNodes.length * this.outputNodes.length;
    maxConnection +=
      (this.hiddenNodes.length * (this.hiddenNodes.length - 1)) / 2;

    return this.connections.length === maxConnection;
  }

  crossover(other: Network): Network {
    const offspring = new Network(this.inputNumber, this.outputNumber, this.innovDB);
    offspring.connections = [];

    const baseParent = this.nextNode >= other.nextNode ? this : other;
    offspring.nextNode = baseParent.nextNode;
    offspring.hiddenNodes = baseParent.hiddenNodes.map((n) => n.clone());

    const isMoreFit = this.getFitness() >= other.getFitness();
    const moreFit = isMoreFit ? this : other;
    const lessFit = isMoreFit ? other : this;

    function findMatching(number: number): Node {
      for (const node of offspring.hiddenNodes.concat(
        offspring.inputNodes,
        offspring.outputNodes
      )) {
        if (node.number === number) return node;
      }

      // IMPOSSIBLE
      console.log("impossible");
      return offspring.inputNodes[0];
    }

    for (const con1 of moreFit.connections) {
      let found = false;
      for (const con2 of lessFit.connections) {
        if (con1.innovNumber === con2.innovNumber) {
          found = true;
          const from = findMatching(con1.from.number);
          const to = findMatching(con1.to.number);

          const rand = Math.random() < 0.5;
          const weight = rand ? con1.weight : con2.weight;
          const enabled = rand ? con1.enabled : con2.enabled;

          offspring.addConnection(
            new Connection(from, to, this.innovDB, weight, enabled, con1.innovNumber)
          );
          break;
        }
      }

      if (!found) {
        const from = findMatching(con1.from.number);
        const to = findMatching(con1.to.number);
        offspring.addConnection(new Connection(from, to, this.innovDB, con1.weight));
      }
    }

    return offspring;
  }

  dump(): void {
    for (const connection of this.connections) {
      console.log(
        `${connection.from.type} ${connection.from.number} -> ${
          connection.to.type
        } ${connection.to.number} ${
          connection.enabled ? connection.weight : "[DISABLED]"
        } ${connection.innovNumber} `
      );
    }
    console.log("-----");
  }

  predict(inputs: number[]): number[] {
    const result = [];
    for (const node of this.sortedNodes) {
      node.clearInput();
    }

    for (const node of this.sortedNodes) {
      if (node.type === "output") result.push(node.getOutput());
      else if (node.type === "input") node.addInput(inputs[node.number], 1);

      for (const connection of this.connections) {
        if (connection.from === node) {
          connection.to.addInput(node.getOutput(), connection.weight);
        }
      }
    }

    return result;
  }

  getFitness(): number {
    if (this.fitness == null) {
      throw new Error("network is not evaluated yet");
    } else {
      return this.fitness;
    }
  }

  setFitness(fitness: number) {
    this.fitness = fitness;
  }

  compatibilityMetric(other: Network, params: NeatParameters): number {
    function sortByInnov(cons: Connection[]): Connection[] {
      return cons.sort((a, b) => a.innovNumber - b.innovNumber);
    }

    const sortedMe = sortByInnov(this.connections),
      sortedOther = sortByInnov(other.connections);
    const lastNum = Math.min(
      sortedMe[sortedMe.length - 1].innovNumber,
      sortedOther[sortedOther.length - 1].innovNumber
    );
    let disjointNum = 0,
      excessNum = 0;
    const weightDiffs = [];

    for (const con of sortedMe) {
      const matching = sortedOther.find(
        (v) => v.innovNumber === con.innovNumber
      );
      if (!matching) {
        if (con.innovNumber > lastNum) excessNum++;
        else disjointNum++;
      } else {
        weightDiffs.push(Math.abs(con.weight - matching.weight));
      }
    }

    const weightDiffAvg =
      weightDiffs.reduce((a, b) => a + b) / weightDiffs.length;
    // const bigN = Math.max(this.connections.length, other.connections.length);

    return (
      params.speciation.c1 * excessNum +
      params.speciation.c2 * disjointNum +
      params.speciation.c3 * weightDiffAvg
    );
  }
}

export default Network;
